<template>
  <div>
    <div class="modal-body">
      <form @submit.prevent="handleSubmit">
        <!-- Step Indicator -->
        <div class="mb-4 text-center">
          <span :class="['step-badge', step === 1 ? 'active' : '']">1</span>
          <span class="mx-2">—</span>
          <span :class="['step-badge', step === 2 ? 'active' : '']">2</span>
        </div>

        <!-- ================= STEP 1 ================= -->
        <div v-if="step === 1">
          <!-- Error Message -->
          <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
          </div>
          <div class="mb-3">
            <label class="form-label">Video Title</label>
            <input
              type="text"
              class="form-control"
              v-model="newVideo.title"
              required
            />
          </div>

          <div class="mb-3">
            <label class="form-label">YouTube Link (Unlisted)</label>
            <input
              type="url"
              class="form-control"
              v-model="newVideo.link"
              required
            />
          </div>

          <div class="mb-3">
            <label class="form-label">Description</label>
            <textarea
              class="form-control"
              rows="4"
              v-model="newVideo.description"
              required
            ></textarea>
          </div>
        </div>

        <!-- ================= STEP 2 ================= -->
        <div v-if="step === 2">
          <div class="mb-3">
            <label class="form-label">Grade</label>
            <select class="form-select" v-model="newVideo.grade_id" required>
              <option value="" disabled>Select Grade</option>
              <option v-for="g in grades" :key="g.id" :value="g.id">
                {{ g.level }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">Subject</label>
            <select
              class="form-select"
              v-model="newVideo.subject_id"
              :disabled="!newVideo.grade_id"
              required
            >
              <option value="" disabled>Select Subject</option>
              <option v-for="s in subjects" :key="s.id" :value="s.id">
                {{ s.name }}
              </option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label">Topic</label>
            <select
              class="form-select"
              v-model="newVideo.topic_id"
              :disabled="!newVideo.subject_id"
              required
            >
              <option value="" disabled>Select Topic</option>
              <option v-for="t in topics" :key="t.id" :value="t.id">
                {{ t.title }}
              </option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">Month</label>
            <select class="form-select" v-model="newVideo.month" required>
              <option value="" disabled>Select Month</option>
              <option v-for="m in months" :key="m.value" :value="m.value">
                {{ m.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- ================= FOOTER ================= -->
        <div class="modal-footer p-0 mt-3">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>

          <!-- Back Button -->
          <button
            v-if="step === 2"
            type="button"
            class="btn btn-outline-secondary"
            @click="step--"
          >
            Back
          </button>

          <!-- Next Button -->
          <button
            v-if="step === 1"
            type="button"
            class="btn btn-primary"
            @click="goToStep2"
          >
            Next
          </button>

          <!-- Save Button -->
          <button
            v-if="step === 2"
            type="submit"
            class="btn btn-pink"
            :disabled="loading"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            {{ loading ? "Saving..." : "Save" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AddVideo",

  data() {
    return {
      step: 1,
      grades: [],
      subjects: [],
      topics: [],
      loading: false,
      errorMessage: "",

      months: [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
      ],

      newVideo: {
        title: "",
        description: "",
        link: "",
        grade_id: "",
        subject_id: "",
        topic_id: "",
        month: "",
        status: "active",
      },
    };
  },

  mounted() {
    this.fetchGrades(); // ✅ only grades load first
  },

  watch: {
    // ✅ When grade changes → load subjects
    "newVideo.grade_id"(newVal) {
      if (newVal) {
        this.fetchSubjectsByGrade();
      }

      // Reset child dropdowns
      this.newVideo.subject_id = "";
      this.newVideo.topic_id = "";
      this.subjects = [];
      this.topics = [];
    },

    // ✅ When subject changes → load topics
    "newVideo.subject_id"(newVal) {
      if (newVal) {
        this.fetchTopicsBySubject();
      }

      // Reset topic
      this.newVideo.topic_id = "";
      this.topics = [];
    },
  },

  methods: {
    goToStep2() {
      if (
        !this.newVideo.title ||
        !this.newVideo.link ||
        !this.newVideo.description
      ) {
        this.errorMessage = "Please complete all fields before continuing.";
        return;
      }
      this.errorMessage = "";
      this.step = 2;
    },

    handleSubmit() {
      if (this.step === 2) {
        this.addVideo();
      }
    },

    async fetchGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async fetchSubjectsByGrade() {
      if (!this.newVideo.grade_id) return;

      try {
        const res = await axios.get(
          `/a-grade-subjects?grade_id=${this.newVideo.grade_id}`,
        );

        this.subjects = res.data.subjects; // 🔥 FIX HERE
      } catch (err) {
        console.error(err);
      }
    },

    async fetchTopicsBySubject() {
      if (!this.newVideo.subject_id) return;

      try {
        const res = await axios.get(
          `/a-get-subject-topics?subject_id=${this.newVideo.subject_id}`,
        );

        this.topics = res.data.topics;
      } catch (err) {
        console.error(err);
      }
    },

    extractYoutubeId(url) {
      const regExp = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/;
      const match = url.match(regExp);
      return match ? match[1] : null;
    },

    async addVideo() {
      this.loading = true;
      this.errorMessage = "";

      try {
        const videoId = this.extractYoutubeId(this.newVideo.link);

        if (!videoId) {
          this.errorMessage = "Invalid YouTube URL.";
          this.step = 1;
          this.loading = false;
          return;
        }

        if (
          !this.newVideo.grade_id ||
          !this.newVideo.subject_id ||
          !this.newVideo.topic_id ||
          !this.newVideo.month
        ) {
          this.errorMessage = "Please select Grade, Subject, Topic and Month.";
          this.step = 2;
          this.loading = false;
          return;
        }

        const payload = {
          topic_id: this.newVideo.topic_id,
          title: this.newVideo.title,
          description: this.newVideo.description,
          video_id: videoId,
          month: this.newVideo.month,
          status: this.newVideo.status,
        };

        await axios.post("/add-video-lesson", payload);

        this.$emit("video-added");
        this.resetForm();
      } catch (err) {
        console.error(err);
        this.errorMessage = "Something went wrong";
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.step = 1;
      this.subjects = [];
      this.topics = [];

      this.newVideo = {
        title: "",
        description: "",
        link: "",
        grade_id: "",
        subject_id: "",
        topic_id: "",
        month: "",
        status: "active",
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.step-badge {
  display: inline-block;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #ddd;
  line-height: 30px;
  font-weight: bold;
}

.step-badge.active {
  background: #e83e8c;
  color: white;
}

.btn-pink {
  background-color: #e83e8c;
  color: #fff;
  border: none;
}
.btn-pink:hover {
  background-color: #d63384;
  color: #fff;
}
</style>
